export const GUILD_RANK_CATALOG = [
  { value: "guild-leader", label: "Guild Leader", order: 0 },
  { value: "vice-leader", label: "Vice Master", order: 1 },
  { value: "mystic-unicorn", label: "Mystic Unicorn", order: 2 },
  { value: "divine-unicorn", label: "Divine Unicorn", order: 3 },
  { value: "golden-unicorn", label: "Golden Unicorn", order: 4 },
  { value: "silver-unicorn", label: "Silver Unicorn", order: 5 },
  { value: "apprentice", label: "Apprentice", order: 6 },
  { value: "unicorn", label: "Unicorn", order: 7 },
  { value: "member", label: "Member", order: 8 },
] as const

export const GUILD_SPECIAL_TITLE_CATALOG = [
  { value: "uni-architect", label: "Uni-Architect", order: 0 },
  { value: "sky-strider", label: "Sky Strider", order: 1 },
] as const

export type GuildRankTitle = (typeof GUILD_RANK_CATALOG)[number]["value"]
export type GuildSpecialTitle =
  (typeof GUILD_SPECIAL_TITLE_CATALOG)[number]["value"]

export const GUILD_RANK_VALUES = GUILD_RANK_CATALOG.map((entry) => entry.value)
export const GUILD_SPECIAL_TITLE_VALUES = GUILD_SPECIAL_TITLE_CATALOG.map(
  (entry) => entry.value,
)

export const GUILD_RANK_LABEL = Object.fromEntries(
  GUILD_RANK_CATALOG.map((entry) => [entry.value, entry.label]),
) as Record<GuildRankTitle, (typeof GUILD_RANK_CATALOG)[number]["label"]>
export const GUILD_RANK_ORDER = Object.fromEntries(
  GUILD_RANK_CATALOG.map((entry) => [entry.value, entry.order]),
) as Record<GuildRankTitle, number>
export const GUILD_SPECIAL_TITLE_LABEL = Object.fromEntries(
  GUILD_SPECIAL_TITLE_CATALOG.map((entry) => [entry.value, entry.label]),
) as Record<
  GuildSpecialTitle,
  (typeof GUILD_SPECIAL_TITLE_CATALOG)[number]["label"]
>
export const GUILD_SPECIAL_TITLE_ORDER = Object.fromEntries(
  GUILD_SPECIAL_TITLE_CATALOG.map((entry) => [entry.value, entry.order]),
) as Record<GuildSpecialTitle, number>

export interface GuildMemberTitles {
  memberId: string
  rankTitle: GuildRankTitle | null
  specialTitles: GuildSpecialTitle[]
}

type GuildMemberTitleInput = Pick<
  GuildMemberTitles,
  "rankTitle" | "specialTitles"
>

const RANK_VALUE = Object.fromEntries(
  GUILD_RANK_VALUES.map((value) => [value, true]),
) as Record<string, true>
const SPECIAL_TITLE_VALUE = Object.fromEntries(
  GUILD_SPECIAL_TITLE_VALUES.map((value) => [value, true]),
) as Record<string, true>

function integrityError(memberId: string, column: string): Error {
  return new Error(
    `Invalid guild member title data for ${memberId} in ${column}`,
  )
}

export function parseGuildMemberTitles(
  memberId: string,
  rankTitle: string | null,
  specialTitles: string[],
): GuildMemberTitles {
  if (rankTitle !== null && !Object.hasOwn(RANK_VALUE, rankTitle)) {
    throw integrityError(memberId, "rank_title")
  }

  if (
    specialTitles.some((title) => !Object.hasOwn(SPECIAL_TITLE_VALUE, title)) ||
    new Set(specialTitles).size !== specialTitles.length
  ) {
    throw integrityError(memberId, "special_titles")
  }

  return {
    memberId,
    rankTitle: rankTitle as GuildRankTitle | null,
    specialTitles: (specialTitles as GuildSpecialTitle[]).toSorted(
      (left, right) =>
        GUILD_SPECIAL_TITLE_ORDER[left] - GUILD_SPECIAL_TITLE_ORDER[right],
    ),
  }
}

export function normalizeGuildMemberTitles(
  input: GuildMemberTitleInput,
): GuildMemberTitleInput {
  if (input.rankTitle !== null && !Object.hasOwn(RANK_VALUE, input.rankTitle)) {
    throw new Error("Invalid guild member rank title")
  }
  if (
    input.specialTitles.some(
      (title) => !Object.hasOwn(SPECIAL_TITLE_VALUE, title),
    )
  ) {
    throw new Error("Invalid guild member special title")
  }

  return {
    rankTitle: input.rankTitle,
    specialTitles: [...new Set(input.specialTitles)].toSorted(
      (left, right) =>
        GUILD_SPECIAL_TITLE_ORDER[left] - GUILD_SPECIAL_TITLE_ORDER[right],
    ),
  }
}
